import { Settings } from './interfaces/interfaces';
import { log } from './domain/log';
import { createCanvas, initBox, handleResize, isDocumentHidden } from './domain/dom-provider';
import { getContext, createShader, createProgram, createShaderAttribute } from './domain/webgl-provider';
import { hexToNormalized } from './domain/color-provider';
import vertex from '../shaders/vertex.glsl';
import cloudsFragment from '../shaders/clouds-fragment.glsl';
import waterFragment from '../shaders/water-fragment.glsl';
import sparklesFragment from '../shaders/sparkles-fragment.glsl';
import rainFragment from '../shaders/rain-fragment.glsl';
import snowFragment from '../shaders/snow-fragment.glsl';
import fireFragment from '../shaders/fire-fragment.glsl';

const directions = ['left', 'right', 'top', 'bottom', 'left-top', 'right-top', 'left-bottom', 'right-bottom'];

/**
 * default settings
 */
const defaults: Settings = {
    debug: false,

    // clouds ------
    cloudsBGColor1: '#006eb2',
    cloudsBGColor2: '#89ccff',
    cloudsColor: '#ffffff',
    cloudsSpeed: 0.5,
    cloudsDirection: 'left',
    cloudsBrightness: 0.7,

    // water --------------
    waterBGColor1: '#2193b0',
    waterBGColor2: '#6dd5ed',
    waterSpeed: 1.0,
    waterDirection: 'left',
    waterRelief: 1.0,
  
    // sparkles --------------
    sparklesRotationDirection: 'left', // left or right
    sparklesRotationSpeed: 3,
    sparklesBGColor: '#000000',
    sparklesColor1: '#236cff',
    sparklesColor2: '#fbff89',
    sparklesDensity: 20,
    sparklesElongation: 5,
    sparklesLightness: 1,

    // rain --------------
    rainSpeed: 4.0,
    rainAngle: 10.0,
    rainColor: '#ffffff',
    dropLength: 1.0,
    dropWidth: 0.005,

    // snow --------------
    snowAngle: 10.0,
    snowSpeed: 0.5,
    snowBGColor: '#000000',
    snowColor: '#ffffff',
    snowDensity: 55,
    flakeSize: 0.1,
    snowLightness: 1.0,

    // fire --------------
    fireBaseColor1: '#bc4c18',
    fireBaseColor2: '#fff200',
    fireShape: 7,
    fireSpeed: 1.0,
    fireStrength: 175,
    fireDetalization: 2
};

/**
 * collect data attributes from HTML
 */
const collectDataAttr = (settings: Settings, $box: HTMLElement) => {

    // clouds --------------------
    settings.cloudsBGColor1 = $box.getAttribute('data-bg-color-1') || settings.cloudsBGColor1;
    settings.cloudsBGColor2 = $box.getAttribute('data-bg-color-2') || settings.cloudsBGColor2;
    settings.cloudsColor = $box.getAttribute('data-clouds-color') || settings.cloudsColor;
    settings.cloudsSpeed = Number($box.getAttribute('data-clouds-speed')) || settings.cloudsSpeed;
    settings.cloudsDirection = $box.getAttribute('data-clouds-direction') || settings.cloudsDirection;
    settings.cloudsBrightness = Number($box.getAttribute('data-clouds-brightness')) || settings.cloudsBrightness;

    // water --------------------
    settings.waterBGColor1 = $box.getAttribute('data-bg-color-1') || settings.waterBGColor1;
    settings.waterBGColor2 = $box.getAttribute('data-bg-color-2') || settings.waterBGColor2;
    settings.waterSpeed = Number($box.getAttribute('data-water-speed')) || settings.waterSpeed;
    settings.waterDirection = $box.getAttribute('data-water-direction') || settings.waterDirection;
    settings.waterRelief = Number($box.getAttribute('data-water-relief')) || settings.waterRelief;

    // sparkles -----------------
    settings.sparklesRotationDirection = $box.getAttribute('data-rotation-direction') || settings.sparklesRotationDirection;
    settings.sparklesRotationSpeed = Number($box.getAttribute('data-rotation-speed')) || settings.sparklesRotationSpeed;
    settings.sparklesBGColor = $box.getAttribute('data-bg-color') || settings.sparklesBGColor;
    settings.sparklesColor1 = $box.getAttribute('data-sparkles-color-1') || settings.sparklesColor1;
    settings.sparklesColor2 = $box.getAttribute('data-sparkles-color-2') || settings.sparklesColor2;
    settings.sparklesDensity = Number($box.getAttribute('data-density')) || settings.sparklesDensity;
    settings.sparklesElongation = Number($box.getAttribute('data-elongation')) || settings.sparklesElongation;
    settings.sparklesLightness = Number($box.getAttribute('data-lightness')) || settings.sparklesLightness;

    // rain -----------------
    settings.rainSpeed = Number($box.getAttribute('data-rain-speed')) || settings.rainSpeed;
    settings.rainAngle = Number($box.getAttribute('data-rain-angle'));
    settings.rainColor = $box.getAttribute('data-rain-color') || settings.rainColor;
    settings.dropLength = Number($box.getAttribute('data-drop-length')) || settings.dropLength;
    settings.dropWidth = Number($box.getAttribute('data-drop-width')) || settings.dropWidth;

    // snow -----------------
    settings.snowAngle = Number($box.getAttribute('data-snow-angle'));
    settings.snowSpeed = Number($box.getAttribute('data-snow-speed')) || settings.snowSpeed;
    settings.snowBGColor = $box.getAttribute('data-bg-color') || settings.snowBGColor;
    settings.snowColor = $box.getAttribute('data-snow-color') || settings.snowColor;
    settings.snowDensity = Number($box.getAttribute('data-density')) || settings.snowDensity;
    settings.flakeSize = Number($box.getAttribute('data-flake-size')) || settings.flakeSize;
    settings.snowLightness = Number($box.getAttribute('data-snow-lightness')) || settings.snowLightness;

    // fire -----------------
    settings.fireBaseColor1 = $box.getAttribute('data-base-color-1') || settings.fireBaseColor1;
    settings.fireBaseColor2 = $box.getAttribute('data-base-color-2') || settings.fireBaseColor2;
    settings.fireShape = Number($box.getAttribute('data-fire-shape')) || settings.fireShape;
    settings.fireSpeed = Number($box.getAttribute('data-fire-speed')) || settings.fireSpeed;
    settings.fireStrength = Number($box.getAttribute('data-fire-strength')) || settings.fireStrength;
    settings.fireDetalization = Number($box.getAttribute('data-fire-detalization')) || settings.fireDetalization;  
};

/**
 * init special uniforms for each type
 */
const initTypesUniforms = (settings: Settings, type: string, gl: WebGLRenderingContextStrict, program: WebGLProgram) => {

    switch(type) {
        case 'clouds': {
            const uBGColor1 = gl.getUniformLocation(program, 'uBGColor1');
            gl.uniform3fv(uBGColor1, new Float32Array(hexToNormalized(settings.cloudsBGColor1)));

            const uBGColor2 = gl.getUniformLocation(program, 'uBGColor2');
            gl.uniform3fv(uBGColor2, new Float32Array(hexToNormalized(settings.cloudsBGColor2)));

            const uCloudColor = gl.getUniformLocation(program, 'uCloudColor');
            gl.uniform3fv(uCloudColor, new Float32Array(hexToNormalized(settings.cloudsColor)));

            const uSpeed = gl.getUniformLocation(program, 'uSpeed');
            gl.uniform1f(uSpeed, settings.cloudsSpeed);

            const uDirection = gl.getUniformLocation(program, 'uDirection');
            gl.uniform1f(uDirection, directions.findIndex(item => item === settings.cloudsDirection));

            const uBrightness = gl.getUniformLocation(program, 'uBrightness');
            gl.uniform1f(uBrightness, settings.cloudsBrightness);
            break;
        }

        case 'water': {
            const uBGColor1 = gl.getUniformLocation(program, 'uBGColor1');
            gl.uniform3fv(uBGColor1, new Float32Array(hexToNormalized(settings.waterBGColor1)));

            const uBGColor2 = gl.getUniformLocation(program, 'uBGColor2');
            gl.uniform3fv(uBGColor2, new Float32Array(hexToNormalized(settings.waterBGColor2)));
            
            const uSpeed = gl.getUniformLocation(program, 'uSpeed');
            gl.uniform1f(uSpeed, settings.waterSpeed);

            const uDirection = gl.getUniformLocation(program, 'uDirection');
            gl.uniform1f(uDirection, directions.findIndex(item => item === settings.waterDirection));

            const uRelief = gl.getUniformLocation(program, 'uRelief');
            gl.uniform1f(uRelief, settings.waterRelief);
            break;
        }

        case 'sparkles': {
            const uDirection = gl.getUniformLocation(program, 'uDirection');
            gl.uniform1f(uDirection, settings.sparklesRotationDirection === 'left' ? -1 : 1);

            const uSpeed = gl.getUniformLocation(program, 'uSpeed');
            gl.uniform1f(uSpeed, settings.sparklesRotationSpeed);

            const uBGColor = gl.getUniformLocation(program, 'uBGColor');
            gl.uniform3fv(uBGColor, new Float32Array(hexToNormalized(settings.sparklesBGColor)));

            const uSparkleColor1 = gl.getUniformLocation(program, 'uSparkleColor1');
            gl.uniform3fv(uSparkleColor1, new Float32Array(hexToNormalized(settings.sparklesColor1)));

            const uSparkleColor2 = gl.getUniformLocation(program, 'uSparkleColor2');
            gl.uniform3fv(uSparkleColor2, new Float32Array(hexToNormalized(settings.sparklesColor2)));

            const uDensity = gl.getUniformLocation(program, 'uDensity');
            gl.uniform1f(uDensity, settings.sparklesDensity);

            const uElongation = gl.getUniformLocation(program, 'uElongation');
            gl.uniform1f(uElongation, settings.sparklesElongation);

            const uLightness = gl.getUniformLocation(program, 'uLightness');
            gl.uniform1f(uLightness, settings.sparklesLightness);
            
            break;
        }

        case 'rain': {
            const uSpeed = gl.getUniformLocation(program, 'uSpeed');
            gl.uniform1f(uSpeed, settings.rainSpeed);

            const uRainAngle = gl.getUniformLocation(program, 'uRainAngle');
            gl.uniform1f(uRainAngle, settings.rainAngle);

            const uRainColor = gl.getUniformLocation(program, 'uRainColor');
            gl.uniform3fv(uRainColor, new Float32Array(hexToNormalized(settings.rainColor)));

            const uDropLength = gl.getUniformLocation(program, 'uDropLength');
            gl.uniform1f(uDropLength, settings.dropLength);

            const uDropWidth = gl.getUniformLocation(program, 'uDropWidth');
            gl.uniform1f(uDropWidth, settings.dropWidth);
            break;
        }

        case 'snow': {
            const uSnowAngle = gl.getUniformLocation(program, 'uSnowAngle');
            gl.uniform1f(uSnowAngle, settings.snowAngle);

            const uSpeed = gl.getUniformLocation(program, 'uSpeed');
            gl.uniform1f(uSpeed, settings.snowSpeed);

            const uBGColor = gl.getUniformLocation(program, 'uBGColor');
            gl.uniform3fv(uBGColor, new Float32Array(hexToNormalized(settings.snowBGColor)));

            const uSnowColor = gl.getUniformLocation(program, 'uSnowColor');
            gl.uniform3fv(uSnowColor, new Float32Array(hexToNormalized(settings.snowColor)));

            const uDensity = gl.getUniformLocation(program, 'uDensity');
            gl.uniform1f(uDensity, settings.snowDensity);

            const uFlakeSize = gl.getUniformLocation(program, 'uFlakeSize');
            gl.uniform1f(uFlakeSize, settings.flakeSize);

            const uSnowLightness = gl.getUniformLocation(program, 'uSnowLightness');
            gl.uniform1f(uSnowLightness, settings.snowLightness);

            break;
        }

        case 'fire': {
            const uBaseColor1 = gl.getUniformLocation(program, 'uBaseColor1');
            gl.uniform3fv(uBaseColor1, new Float32Array(hexToNormalized(settings.fireBaseColor1)));

            const uBaseColor2 = gl.getUniformLocation(program, 'uBaseColor2');
            gl.uniform3fv(uBaseColor2, new Float32Array(hexToNormalized(settings.fireBaseColor2)));

            const uFireShape = gl.getUniformLocation(program, 'uFireShape');
            gl.uniform1f(uFireShape, settings.fireShape);

            const uSpeed = gl.getUniformLocation(program, 'uSpeed');
            gl.uniform1f(uSpeed, settings.fireSpeed);

            const uFireStrength = gl.getUniformLocation(program, 'uFireStrength');
            gl.uniform1f(uFireStrength, settings.fireStrength);

            const uFireDetalization = gl.getUniformLocation(program, 'uFireDetalization');
            gl.uniform1f(uFireDetalization, settings.fireDetalization);

            break;
        }
    }
};

/**
 * entry point
 * @param {object} settings
 */
export const main = (settings: Settings, $box: HTMLElement, type: string) => {

    // used to pause fragment shader;
    // the requestAnimationFrame is still running
    let isRunning = true;

    // if true, the whole animation should be stopped;
    // used when the whole program should be replaced with another instance
    let stop = false;

    // animation start time
    let startTime: number = undefined;

    // combine user settings with default settings
    settings = {...defaults, ...settings };

    // collect data attributes from HTML
    collectDataAttr(settings, $box);
    
    // check supported types
    const supported = ['clouds', 'water', 'sparkles', 'rain', 'snow', 'fire'];
    if(supported.indexOf(type) === -1) {
        if(settings.debug){
            log(`The type "${type}" is missing or not supported.`);
        }
        return;
    }

    // add placeholder styles, attributes etc.
    initBox($box);
    
    // generate canvas element (if not generated before)
    const $canvas = createCanvas($box);
    if(!$canvas) return;

    // create webgl context
    const gl = getContext(settings, $canvas) as any as WebGLRenderingContextStrict;
    if(!gl) return;

    // define webgl viewport
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);

    // init shaders
    const vertexShader = createShader(settings, gl, gl.VERTEX_SHADER, vertex);
    let fShaderCode = null;
   
    switch(type) {
        case 'clouds': {
            fShaderCode = cloudsFragment;
            break;
        }

        case 'water': {
            fShaderCode = waterFragment;
            break;
        }

        case 'sparkles': {
            fShaderCode = sparklesFragment;
            break;
        }

        case 'rain': {
            fShaderCode = rainFragment;
            break;
        }

        case 'snow': {
            fShaderCode = snowFragment;
            break;
        }

        case 'fire': {
            fShaderCode = fireFragment;
            break;
        }
    }
    
    const fragmentShader = createShader(settings, gl, gl.FRAGMENT_SHADER, fShaderCode);

    // compile shader
    const program = createProgram(settings, gl, [vertexShader, fragmentShader]);
    gl.useProgram(program);

    // init position attribute: 3 dots by (x, y)
    const aPosition = new Float32Array([
        -1,-1,  1, 1, 
         1,-1, -1,-1, 
         1, 1, -1, 1]);
    createShaderAttribute(settings, gl, program, 'aPosition', 2, 0, 0, aPosition); 

    // init resolution uniform
    const uResolution = gl.getUniformLocation(program, 'uResolution');
    gl.uniform2fv(uResolution, new Float32Array([gl.drawingBufferWidth, gl.drawingBufferHeight]));

    // init time uniform
    const uTime = gl.getUniformLocation(program, 'uTime');

    // init 'isRunning' uniform
    const uRunning = gl.getUniformLocation(program, 'uRunning');

    // init special uniforms for each type
    initTypesUniforms(settings, type, gl, program);

    /**
     * draw
     */
    const draw = (timestamp: number) => {

        if(timestamp === undefined) return;

        if(!stop){

            // the first call
             if(startTime === undefined){
                startTime = timestamp;
            }

            const uTimeValue = (timestamp - startTime) / 1000; // seconds

            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.uniform1f(uTime, timestamp / 1000);
            gl.uniform1f(uRunning, isRunning ? 1 : 0);
            gl.drawArrays(gl.TRIANGLES, 0, 6);

             // if the time > 10 min -> reset the timer
             if(uTimeValue > 10 * 60) {
                startTime = undefined;
            }
        
            requestAnimationFrame(draw);
        }
    };
    requestAnimationFrame(draw);

    // handle window resize 
    const resizeObserver = handleResize($box, $canvas, draw, gl, uResolution);

    // handle visibility change
    document.addEventListener('visibilitychange', () => {
        isRunning = !isDocumentHidden();
    });

    /**
     * APIs
     */
    return {

        /**
         * update any property in the settings object 
         */
        set: (property: string, value: any) => {
            settings[property] = value;
            initTypesUniforms(settings, type, gl, program);
        },

        /**
         * remove event listeners
         */
        removeListeners: () => {

            stop = true;

            // stop resize observer if needed
            if(resizeObserver){
                resizeObserver.disconnect()
            }
        }
    };
};
